import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import usageNudgeApi from "../../../api/analytics/usageNudgeApi";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { UsageNudgeAudience } from "../../../types/nudges/NudgeDto";
import { UsageNudgePreviewDto } from "../../../api/analytics/usageNudgeApi";
import { ModalPopup } from "../../common";
import SmallLoader from "../../loaders/SmallLoader";
import Toast from "../../common/Toast";
import { ToastConfig } from "../../common/ToastConfig";
import {
  formatUsageNudgeAge,
  formatUsageNudgeCooldown,
  getUsageNudgeDeliveryChannelLabel,
  formatUsageNudgeRetryDay,
} from "../../../helpers/usage/usageNudgeHelpers";

interface UsageNudgeComposeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AnalyticsFilterInputs | null;
  recipientEmployeeId: number | null;
  audience: UsageNudgeAudience;
  fallbackRecipientName?: string;
  onSent?: () => void;
  onSendStarted?: () => void;
  onSendFailed?: () => void;
  onCooldown?: (lastSentAt: string, senderName: string | null) => void;
}

function UsageNudgeComposeModal({
  isOpen,
  onOpenChange,
  filters,
  recipientEmployeeId,
  audience,
  fallbackRecipientName,
  onSent,
  onSendStarted,
  onSendFailed,
  onCooldown,
}: UsageNudgeComposeModalProps) {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const api = useMemo(
    () => new usageNudgeApi(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const [preview, setPreview] = useState<UsageNudgePreviewDto | null>(null);
  const [adminMessage, setAdminMessage] = useState("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [toastConfig, setToastConfig] = useState(ToastConfig.hidden());
  const requestGenerationRef = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      setPreview(null);
      setAdminMessage("");
      setPreviewError(false);
      setIsLoadingPreview(false);
      setIsSending(false);
      return;
    }

    if (!filters || !recipientEmployeeId) return;

    const requestGeneration = ++requestGenerationRef.current;
    setIsLoadingPreview(true);
    setPreviewError(false);
    setPreview(null);

    api
      .preview(filters, recipientEmployeeId, audience)
      .then((response) => {
        if (requestGeneration !== requestGenerationRef.current) return;

        if (response.status === "OK") {
          setPreview(response.preview);
          setPreviewError(false);
          return;
        }

        setPreviewError(true);
      })
      .catch(() => {
        if (requestGeneration !== requestGenerationRef.current) return;
        setPreviewError(true);
      })
      .finally(() => {
        if (requestGeneration !== requestGenerationRef.current) return;
        setIsLoadingPreview(false);
      });
  }, [api, audience, filters, isOpen, recipientEmployeeId]);

  const recipientName =
    preview?.recipientName ||
    fallbackRecipientName ||
    t("Common.Unknown", "Unknown");

  const title =
    audience === "MANAGER"
      ? t("Analytics.Widgets.Usage.Nudge.ComposeTitleManager", "Nudge manager")
      : t(
          "Analytics.Widgets.Usage.Nudge.ComposeTitleEmployee",
          "Nudge employee",
        );

  const description =
    audience === "MANAGER"
      ? t(
          "Analytics.Widgets.Usage.Nudge.ComposeDescriptionManager",
          "Send a reminder about all outstanding journeys for this manager's team within the selected journeys and date range.",
        )
      : t(
          "Analytics.Widgets.Usage.Nudge.ComposeDescriptionEmployee",
          "Send a reminder about this employee's outstanding journeys within the selected journeys and date range.",
        );

  const handleSend = async () => {
    if (!filters || !recipientEmployeeId || !preview || isSending) return;

    setIsSending(true);
    onSendStarted?.();
    const response = await api.send(
      filters,
      recipientEmployeeId,
      audience,
      adminMessage,
    );
    setIsSending(false);

    if (response.status === "OK") {
      if (response.result.deliveryFailed) {
        setToastConfig(
          ToastConfig.error(
            t(
              "Analytics.Widgets.Usage.Nudge.SuccessDeliveryFailed",
              "Nudge recorded, but delivery failed. Try again later or contact support.",
            ),
            "",
          ),
        );
      } else {
        setToastConfig(
          ToastConfig.success(
            t("Analytics.Widgets.Usage.Nudge.Success", "Nudge sent."),
            "",
          ),
        );
      }
      onSent?.();
      onOpenChange(false);
      return;
    }

    if (response.status === "COOLDOWN_ACTIVE") {
      onCooldown?.(response.lastSentAt, response.senderName);
      setPreview((current) =>
        current
          ? {
              ...current,
              canSend: false,
              cooldownEndsAt: response.cooldownEndsAt,
            }
          : current,
      );
      setToastConfig(
        ToastConfig.error(
          t("Analytics.Widgets.Usage.Nudge.CooldownConflict", {
            defaultValue:
              "Nudged {{age}} - you can nudge again on {{retryDay}}",
            age: formatUsageNudgeAge(response.lastSentAt),
            retryDay: formatUsageNudgeRetryDay(
              response.cooldownEndsAt,
              i18n.language,
            ),
          }),
          "",
        ),
      );
      return;
    }

    if (response.status === "DELIVERY_FAILED") {
      setToastConfig(
        ToastConfig.error(
          t(
            "Analytics.Widgets.Usage.Nudge.SuccessDeliveryFailed",
            "Nudge recorded, but delivery failed. Try again later or contact support.",
          ),
          "",
        ),
      );
      onSent?.();
      onOpenChange(false);
      return;
    }

    setToastConfig(
      ToastConfig.error(
        t(
          "Analytics.Widgets.Usage.Nudge.Error",
          "The nudge could not be sent. Try again.",
        ),
        "",
      ),
    );
    onSendFailed?.();
  };

  const cooldownLabel =
    preview?.cooldownEndsAt &&
    t("Analytics.Widgets.Usage.Nudge.CooldownMessage", {
      defaultValue: "You can nudge this person again after {{dateTime}}.",
      dateTime: formatUsageNudgeCooldown(preview.cooldownEndsAt, i18n.language),
    });

  return (
    <>
      <ModalPopup
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title={title}
        titleDescription={description}
        width="SMALL"
        primaryButtonText={
          isSending
            ? t("Analytics.Widgets.Usage.Nudge.Sending", "Sending...")
            : t("Analytics.Widgets.Usage.Nudge.Send", "Send nudge")
        }
        secondaryButtonText={t(
          "Analytics.Widgets.Usage.Nudge.Cancel",
          "Cancel",
        )}
        onPrimaryButtonClick={handleSend}
        onSecondaryButtonClick={() => onOpenChange(false)}
        isLoading={isSending}
        isPrimaryButtonDisabled={
          isLoadingPreview || previewError || !preview || isSending
        }
      >
        {isLoadingPreview && (
          <div className="py-6">
            <SmallLoader />
            <p className="pt-2 text-center text-sm text-gray-600">
              {t(
                "Analytics.Widgets.Usage.Nudge.LoadingPreview",
                "Loading nudge details...",
              )}
            </p>
          </div>
        )}

        {!isLoadingPreview && previewError && (
          <p className="text-sm text-red-600">
            {t(
              "Analytics.Widgets.Usage.Nudge.PreviewError",
              "Could not load nudge details. Try again.",
            )}
          </p>
        )}

        {!isLoadingPreview && preview && !previewError && (
          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <p className="font-semibold text-gray-800">
                {t("Analytics.Widgets.Usage.Nudge.RecipientLabel", "Recipient")}
              </p>
              <p>{recipientName}</p>
            </div>

            <div>
              <p className="font-semibold text-gray-800">
                {t("Analytics.Widgets.Usage.Nudge.OutstandingCount", {
                  defaultValue: "{{count}} outstanding",
                  count: preview.outstandingCount,
                })}
              </p>
            </div>

            {preview.previewLines.length > 0 && (
              <div>
                <p className="font-semibold text-gray-800">
                  {t(
                    "Analytics.Widgets.Usage.Nudge.OutstandingListHeading",
                    "Included in this nudge",
                  )}
                </p>
                <ul className="mt-2 max-h-40 list-disc space-y-1 overflow-y-auto pl-5">
                  {preview.previewLines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                {preview.remainingPreviewCount > 0 && (
                  <p className="mt-2 text-gray-600">
                    {t("Analytics.Widgets.Usage.Nudge.RemainingPreview", {
                      defaultValue: "and {{count}} more",
                      count: preview.remainingPreviewCount,
                    })}
                  </p>
                )}
              </div>
            )}

            <div>
              <p className="font-semibold text-gray-800">
                {t(
                  "Analytics.Widgets.Usage.Nudge.DeliveryChannelLabel",
                  "Delivery channel",
                )}
              </p>
              <p>
                {getUsageNudgeDeliveryChannelLabel(preview.deliveryChannel, t)}
              </p>
            </div>

            {!preview.canSend && cooldownLabel && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-900">
                {cooldownLabel}
              </p>
            )}

            {preview.canSend && (
              <label className="block">
                <span className="mb-1 block font-semibold text-gray-800">
                  {t(
                    "Analytics.Widgets.Usage.Nudge.MessageLabel",
                    "Optional message",
                  )}
                </span>
                <textarea
                  value={adminMessage}
                  onChange={(event) => setAdminMessage(event.target.value)}
                  rows={4}
                  maxLength={4000}
                  placeholder={t(
                    "Analytics.Widgets.Usage.Nudge.MessagePlaceholder",
                    "Add a short note for the recipient (optional)",
                  )}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:not-italic focus:border-gray-400 focus:outline-none focus:ring-0"
                />
              </label>
            )}
          </div>
        )}
      </ModalPopup>

      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </>
  );
}

export default UsageNudgeComposeModal;
